// src/pages/MessageDetails.jsx
// ============================================================================
// 🎯 PÁGINA: MessageDetails — V17 ENTERPRISE ULTRA PREMIUM
// 📋 Função: Visualização detalhada de uma mensagem individual
// 📱 Mobile: Layout editorial vertical · Navegação por gestos · App nativa
// 🖥️ Desktop: Layout premium com cards · Breadcrumb · Sidebar informativa
// 🎨 Design: Glassmorphism · Gradientes animados · Micro-interações
// 🔒 Robusto: Anti-crash · Fallbacks em cascata · Modal de exclusão unificado
// ============================================================================
// 🔥 V17: Breadcrumb inteligente com link para histórico do cliente
// 🔥 V17: Botão "Ver conversa completa" que abre MessageHistoryModal
// 🔥 V17: DeleteConfirmModal unificado (componente reutilizável)
// 🔥 V17: Compartilhar nativo (Web Share API) + fallback clipboard
// 🔥 V17: Markdown com syntax highlighting melhorado
// 🔥 V17: Estados: loading skeleton · empty · error · sucesso
// ============================================================================

import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
  memo,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  User,
  Phone,
  Calendar,
  Clock,
  CheckCircle,
  Check,
  AlertCircle,
  Send,
  MessageSquare,
  Copy,
  Trash2,
  Share2,
  Download,
  Mail,
  MapPin,
  Tag,
  Sparkles,
  Zap,
  Globe,
  Shield,
  Loader2,
  ChevronRight,
  Home,
  History,
  ExternalLink,
  Edit3,
  Forward,
  MoreHorizontal,
} from 'lucide-react';
import { useAuth } from '../contexts/useAuth';
import { useNotifications } from '../contexts/NotificationsContext';
import { useIsMobile } from '../hooks/useMediaQuery';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Componentes compartilhados
import DeleteConfirmModal from '../components/mensagens/DeleteConfirmModal';
import MessageHistoryModal from '../components/mensagens/MessageHistoryModal';
import MessageActionMenu from '../components/mensagens/MessageActionMenu';
import {
  getMessageActionPermissions,
  formatFullDateTime,
  truncateText,
  getMessageClipboardText,
} from '../components/mensagens/helpers';

// DataContext
import DataContext from '../lib/data/context/DataContext';

// ============================================================================
// 🎨 CONSTANTES
// ============================================================================
const PRIMARY_COLOR = '#10b981';

// ============================================================================
// 🎯 SUBCOMPONENTE: LoadingSkeleton (memoizado)
// ============================================================================
const LoadingSkeleton = memo(({ isMobile }) => (
  <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
    <div className={`max-w-4xl mx-auto ${isMobile ? 'p-3' : 'p-6'}`}>
      {/* Breadcrumb skeleton */}
      <div className="flex items-center gap-2 mb-6">
        <div className="w-6 h-4 bg-muted rounded animate-pulse" />
        <div className="w-4 h-4 bg-muted rounded animate-pulse" />
        <div className="w-24 h-4 bg-muted rounded animate-pulse" />
      </div>

      {/* Status card skeleton */}
      <div className="bg-card/80 backdrop-blur-sm rounded-2xl border border-color p-6 mb-4 animate-pulse">
        <div className="h-2 w-full bg-muted rounded-full mb-6" />
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-muted rounded-xl" />
          <div className="space-y-2 flex-1">
            <div className="h-5 w-40 bg-muted rounded" />
            <div className="h-4 w-60 bg-muted rounded" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-16 bg-muted rounded-xl" />
          ))}
        </div>
      </div>

      {/* Content card skeleton */}
      <div className="bg-card/80 backdrop-blur-sm rounded-2xl border border-color p-6 animate-pulse">
        <div className="h-5 w-32 bg-muted rounded mb-4" />
        <div className="space-y-3">
          <div className="h-4 bg-muted rounded w-full" />
          <div className="h-4 bg-muted rounded w-3/4" />
          <div className="h-4 bg-muted rounded w-1/2" />
          <div className="h-4 bg-muted rounded w-5/6" />
        </div>
      </div>
    </div>
  </div>
));
LoadingSkeleton.displayName = 'LoadingSkeleton';

// ============================================================================
// 🎯 SUBCOMPONENTE: ErrorState (memoizado)
// ============================================================================
const ErrorState = memo(({ onRetry, onBack, isMobile }) => (
  <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: 'spring', damping: 20, stiffness: 200 }}
      className="bg-card rounded-2xl shadow-xl border border-color p-8 max-w-md mx-4 text-center"
    >
      <motion.div
        animate={{ rotate: [0, 10, -10, 0] }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="w-20 h-20 bg-rose-100 rounded-2xl flex items-center justify-center mx-auto mb-5"
      >
        <AlertCircle className="w-10 h-10 text-rose-500" />
      </motion.div>
      <h2 className="text-xl font-bold text-primary mb-2">
        Mensagem não encontrada
      </h2>
      <p className="text-muted text-sm mb-6 leading-relaxed">
        A mensagem pode ter sido excluída ou ainda está sendo sincronizada com
        o servidor.
      </p>
      <div className="flex gap-3 justify-center">
        <button
          onClick={onRetry}
          className="px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 text-sm font-semibold flex items-center gap-2 transition-all active:scale-95 shadow-md"
        >
          <Loader2 className="w-4 h-4" />
          Tentar novamente
        </button>
        <button
          onClick={onBack}
          className="px-5 py-2.5 border border-color rounded-xl hover:bg-muted text-sm font-semibold text-secondary transition-all active:scale-95"
        >
          Voltar
        </button>
      </div>
    </motion.div>
  </div>
));
ErrorState.displayName = 'ErrorState';

// ============================================================================
// 🎯 SUBCOMPONENTE: Breadcrumb (memoizado)
// ============================================================================
const Breadcrumb = memo(({ client, navigate, isMobile }) => (
  <motion.div
    initial={{ opacity: 0, y: -8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.05 }}
    className={`
      flex items-center gap-1.5 flex-wrap
      ${isMobile ? 'mb-3 text-[10px]' : 'mb-5 text-xs'}
    `}
  >
    <button
      onClick={() => navigate('/')}
      className="text-muted hover:text-secondary transition-colors flex items-center gap-1"
    >
      <Home size={isMobile ? 12 : 14} />
      <span className="hidden sm:inline">Início</span>
    </button>
    <ChevronRight size={isMobile ? 10 : 12} className="text-slate-300" />
    <button
      onClick={() => navigate('/mensagens')}
      className="text-muted hover:text-secondary transition-colors"
    >
      Mensagens
    </button>
    {client && (
      <>
        <ChevronRight size={isMobile ? 10 : 12} className="text-slate-300" />
        <button
          onClick={() => navigate(`/clientes/${client.id}`)}
          className="text-muted hover:text-secondary transition-colors font-medium truncate max-w-[120px]"
        >
          {client.name}
        </button>
      </>
    )}
    <ChevronRight size={isMobile ? 10 : 12} className="text-slate-300" />
    <span className="text-secondary font-semibold">Detalhes</span>
  </motion.div>
));
Breadcrumb.displayName = 'Breadcrumb';

// ============================================================================
// 🎯 SUBCOMPONENTE: StatusBadge (memoizado)
// ============================================================================
const StatusBadge = memo(({ status }) => {
  const config = {
    read: {
      label: 'Lida',
      icon: CheckCircle,
      gradient: 'from-blue-500 to-indigo-500',
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-700',
      description: 'Mensagem visualizada pelo cliente',
    },
    delivered: {
      label: 'Entregue',
      icon: Check,
      gradient: 'from-green-500 to-emerald-500',
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-700',
      description: 'Mensagem entregue no dispositivo',
    },
    sent: {
      label: 'Enviada',
      icon: Send,
      gradient: 'from-gray-500 to-slate-500',
      bg: 'bg-gray-50',
      border: 'border-gray-200',
      text: 'text-gray-700',
      description: 'Mensagem enviada com sucesso',
    },
    failed: {
      label: 'Falhou',
      icon: AlertCircle,
      gradient: 'from-red-500 to-rose-500',
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-700',
      description: 'Falha no envio da mensagem',
    },
  };

  const info = config[status] || {
    label: 'Processando',
    icon: Clock,
    gradient: 'from-yellow-500 to-amber-500',
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    text: 'text-yellow-700',
    description: 'Mensagem em processamento',
  };

  const Icon = info.icon;

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${info.bg} border ${info.border}`}>
      <Icon size={14} className={info.text} />
      <span className={`text-xs font-semibold ${info.text}`}>{info.label}</span>
    </div>
  );
});
StatusBadge.displayName = 'StatusBadge';

// ============================================================================
// 🎯 COMPONENTE PRINCIPAL V17
// ============================================================================
export default function MessageDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { database, user } = useAuth();
  const { showToast } = useNotifications();
  const isMobile = useIsMobile();

  // ==========================================================================
  // 🔐 AUTENTICAÇÃO
  // ==========================================================================
  const authUserId = useMemo(() => {
    try {
      const stored = localStorage.getItem('auth_user');
      return stored ? JSON.parse(stored).id : user?.uid || null;
    } catch {
      return user?.uid || null;
    }
  }, [user?.uid]);

  // ==========================================================================
  // 📦 DATACONTEXT
  // ==========================================================================
  const ctxRef = useRef(null);
  const [ctxReady, setCtxReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const initCtx = async () => {
      if (!authUserId) {
        if (!cancelled) setCtxReady(true);
        return;
      }
      try {
        const ctx = await DataContext.getInstance(authUserId);
        if (!cancelled && ctx) {
          ctxRef.current = ctx;
          console.log('✅ [MessageDetails V17] DataContext inicializado');
        }
      } catch (e) {
        console.warn('⚠️ [MessageDetails V17] DataContext:', e.message);
      } finally {
        if (!cancelled) setCtxReady(true);
      }
    };

    initCtx();
    return () => {
      cancelled = true;
    };
  }, [authUserId]);

  const getContext = useCallback(() => ctxRef.current || null, []);

  // ==========================================================================
  // 📊 ESTADOS
  // ==========================================================================
  const [message, setMessage] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [hoveredSection, setHoveredSection] = useState(null);

  // Modal de exclusão
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Modal de histórico (ver conversa completa)
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [conversationMessages, setConversationMessages] = useState([]);

  // Menu contextual
  const [contextMenu, setContextMenu] = useState({
    isOpen: false,
    message: null,
  });
  const contextMenuTriggerRef = useRef(null);

  // ==========================================================================
  // 🎨 WHITE LABEL
  // ==========================================================================
  const primaryColor = useMemo(() => {
    try {
      return (
        getComputedStyle(document.documentElement)
          .getPropertyValue('--primary-color')
          .trim() || PRIMARY_COLOR
      );
    } catch {
      return PRIMARY_COLOR;
    }
  }, []);

  // ==========================================================================
  // 📥 CARREGAR MENSAGEM
  // ==========================================================================
  const loadMessage = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setLoadError(false);

    try {
      const ctx = getContext();
      let foundMessage = null;

      // Estratégia 1: DataContext
      if (ctx?.collections?.messages?.findById) {
        try {
          foundMessage = await ctx.collections.messages.findById(id);
        } catch (e) {
          console.warn('⚠️ DataContext.findById:', e.message);
        }
      }

      // Estratégia 2: database.js
      if (!foundMessage && database?.getMessages) {
        try {
          const allMessages = (await database.getMessages()) || [];
          foundMessage = allMessages.find(
            (m) => String(m.id) === String(id) || m._id === id
          );
        } catch (e) {
          console.warn('⚠️ database.getMessages:', e.message);
        }
      }

      // Estratégia 3: localStorage
      if (!foundMessage) {
        try {
          const uid = authUserId || database?.userId;
          if (uid) {
            const stored = JSON.parse(
              localStorage.getItem(`imoveispro_db_user_${uid}`) || '{}'
            );
            const msgs = Array.isArray(stored.messages) ? stored.messages : [];
            foundMessage = msgs.find(
              (m) => String(m.id) === String(id) || m._id === id
            );
          }
        } catch {}
      }

      if (!foundMessage) {
        setLoadError(true);
        showToast('Mensagem não encontrada', 'error');
        return;
      }

      setMessage(foundMessage);

      // Carregar cliente vinculado
      if (foundMessage.clientId) {
        let clientData = null;

        try {
          if (ctx?.collections?.clients?.findById) {
            clientData = await ctx.collections.clients.findById(
              foundMessage.clientId
            );
          }
        } catch {}

        if (!clientData && database?.getClientById) {
          try {
            clientData = await database.getClientById(foundMessage.clientId);
          } catch {}
        }

        setClient(clientData);
      }

      // Carregar mensagens da conversa para o histórico rápido
      if (foundMessage.clientId) {
        try {
          let convMsgs = [];
          if (ctx?.collections?.messages?.read) {
            const all = safeArray(await ctx.collections.messages.read());
            convMsgs = all.filter(
              (m) => m.clientId === foundMessage.clientId
            );
          } else if (database?.getMessages) {
            const all = safeArray(await database.getMessages());
            convMsgs = all.filter(
              (m) => m.clientId === foundMessage.clientId
            );
          }
          setConversationMessages(convMsgs);
        } catch {}
      }
    } catch (error) {
      console.error('❌ [V17 MessageDetails] Erro:', error);
      setLoadError(true);
      showToast('Erro ao carregar mensagem', 'error');
    } finally {
      setLoading(false);
    }
  }, [id, authUserId, getContext, database, showToast]);

  useEffect(() => {
    if (ctxReady) {
      loadMessage();
    }
  }, [ctxReady, loadMessage]);

  // ==========================================================================
  // 🛠️ UTILITÁRIOS
  // ==========================================================================
  function safeArray(arr) {
    return Array.isArray(arr) ? arr : [];
  }

  const formatDateTime = useCallback((timestamp) => {
    if (!timestamp) return 'Data desconhecida';
    try {
      return new Date(timestamp).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Data inválida';
    }
  }, []);

  const getRelativeTime = useCallback(
    (timestamp) => {
      if (!timestamp) return '';
      const now = Date.now();
      const diff = now - timestamp;

      const minutes = Math.floor(diff / 60000);
      if (minutes < 1) return 'agora mesmo';
      if (minutes < 60)
        return `há ${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`;

      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `há ${hours} ${hours === 1 ? 'hora' : 'horas'}`;

      const days = Math.floor(hours / 24);
      if (days < 7) return `há ${days} ${days === 1 ? 'dia' : 'dias'}`;

      return formatDateTime(timestamp);
    },
    [formatDateTime]
  );

  // ==========================================================================
  // 🔘 HANDLERS
  // ==========================================================================
  const copyToClipboard = useCallback(
    async (text) => {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        showToast('Mensagem copiada!', 'success');
        setTimeout(() => setCopied(false), 2000);
      } catch {
        showToast('Não foi possível copiar', 'error');
      }
    },
    [showToast]
  );

  const shareMessage = useCallback(async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Mensagem',
          text: message?.message || '',
        });
        showToast('Compartilhado!', 'success');
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Erro ao compartilhar:', err);
        }
      }
    } else {
      if (message?.message) copyToClipboard(message.message);
    }
  }, [message, copyToClipboard, showToast]);

  const downloadMessage = useCallback(() => {
    if (!message?.message) {
      showToast('Nenhum conteúdo para baixar', 'error');
      return;
    }
    try {
      const blob = new Blob([message.message], {
        type: 'text/plain;charset=utf-8',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mensagem-${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast('Mensagem baixada!', 'success');
    } catch {
      showToast('Erro ao baixar mensagem', 'error');
    }
  }, [message, showToast]);

  // ==========================================================================
  // 🗑️ EXCLUSÃO
  // ==========================================================================
  const handleDeleteClick = useCallback(() => {
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!message?.id) return;

    setDeleting(true);

    try {
      const ctx = getContext();

      if (ctx?.collections?.messages?.delete) {
        await ctx.collections.messages.delete(message.id);
      } else if (database?.deleteMessage) {
        await database.deleteMessage(message.id);
      } else {
        throw new Error('Nenhuma fonte de dados disponível');
      }

      showToast('✅ Mensagem excluída com sucesso!', 'success');
      navigate('/mensagens', { replace: true });
    } catch (error) {
      console.error('❌ [V17] Erro ao excluir:', error);
      showToast('Erro ao excluir mensagem', 'error');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  }, [message, getContext, database, navigate, showToast]);

  // ==========================================================================
  // 📜 VER CONVERSA COMPLETA
  // ==========================================================================
  const handleViewFullConversation = useCallback(() => {
    if (client && conversationMessages.length > 0) {
      setShowHistoryModal(true);
    } else {
      showToast('Nenhuma conversa disponível', 'info');
    }
  }, [client, conversationMessages, showToast]);

  // ==========================================================================
  // 🎮 MENU CONTEXTUAL
  // ==========================================================================
  const handleOpenContextMenu = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    contextMenuTriggerRef.current = e.currentTarget;
    setContextMenu({ isOpen: true, message });
  }, [message]);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu({ isOpen: false, message: null });
  }, []);

  const handleContextEdit = useCallback(() => {
    handleCloseContextMenu();
    showToast('Edição disponível no histórico da conversa', 'info');
  }, [handleCloseContextMenu, showToast]);

  const handleContextResend = useCallback(() => {
    handleCloseContextMenu();
    if (message) {
      const phone = message.phone || client?.phone;
      if (phone) {
        const url = `https://wa.me/${phone.replace(/\D/g, '')}?text=${encodeURIComponent(message.message || '')}`;
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    }
  }, [handleCloseContextMenu, message, client]);

  // ==========================================================================
  // 🎨 LOADING
  // ==========================================================================
  if (loading) {
    return <LoadingSkeleton isMobile={isMobile} />;
  }

  // ==========================================================================
  // 🎨 ERRO
  // ==========================================================================
  if (loadError || !message) {
    return (
      <ErrorState
        onRetry={loadMessage}
        onBack={() => navigate('/mensagens')}
        isMobile={isMobile}
      />
    );
  }

  const permissions = getMessageActionPermissions(message);

  // ==========================================================================
  // 🖼️ RENDER PRINCIPAL
  // ==========================================================================
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <div className={`max-w-4xl mx-auto ${isMobile ? 'px-3 py-4' : 'px-6 py-8'}`}>
        {/* ===== BREADCRUMB ===== */}
        <Breadcrumb client={client} navigate={navigate} isMobile={isMobile} />

        {/* ===== HEADER ===== */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
          className="flex items-center justify-between mb-5"
        >
          <div className="flex items-center gap-3">
            <motion.button
              whileHover={{ scale: 1.08, x: -4 }}
              whileTap={{ scale: 0.94 }}
              onClick={() => navigate(-1)}
              className={`
                p-2.5 bg-card/90 backdrop-blur-sm border border-color
                rounded-xl shadow-md hover:shadow-lg transition-all
                ${isMobile ? 'p-2' : ''}
              `}
              aria-label="Voltar"
            >
              <ArrowLeft size={isMobile ? 18 : 20} className="text-secondary" />
            </motion.button>

            <div>
              <h1
                className={`
                  font-bold text-primary
                  ${isMobile ? 'text-lg' : 'text-2xl'}
                `}
              >
                Detalhes da Mensagem
              </h1>
              <p className="text-xs text-muted flex items-center gap-1.5 mt-0.5">
                <Clock size={11} />
                {getRelativeTime(message.createdAt)}
              </p>
            </div>
          </div>

          {/* Ações rápidas header */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => message?.message && copyToClipboard(message.message)}
              className={`
                p-2 rounded-xl transition-all active:scale-95
                ${copied
                  ? 'bg-green-50 text-green-600'
                  : 'bg-card/80 border border-color text-secondary hover:bg-muted shadow-sm'
                }
              `}
              aria-label="Copiar mensagem"
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </button>

            <button
              onClick={shareMessage}
              className="p-2 bg-card/80 border border-color rounded-xl text-secondary hover:bg-muted shadow-sm transition-all active:scale-95"
              aria-label="Compartilhar"
            >
              <Share2 size={16} />
            </button>

            <button
              onClick={downloadMessage}
              className="p-2 bg-card/80 border border-color rounded-xl text-secondary hover:bg-muted shadow-sm transition-all active:scale-95"
              aria-label="Download"
            >
              <Download size={16} />
            </button>

            {/* Menu contextual trigger (desktop) */}
            {!isMobile && (
              <button
                onClick={handleOpenContextMenu}
                className="p-2 bg-card/80 border border-color rounded-xl text-secondary hover:bg-muted shadow-sm transition-all active:scale-95"
                aria-label="Mais ações"
              >
                <MoreHorizontal size={16} />
              </button>
            )}
          </div>
        </motion.div>

        {/* ===== CARD DE STATUS ===== */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          onMouseEnter={() => setHoveredSection('status')}
          onMouseLeave={() => setHoveredSection(null)}
          className="relative group mb-4"
        >
          {/* Glow hover */}
          <motion.div
            animate={{
              background:
                hoveredSection === 'status'
                  ? `radial-gradient(circle at 50% 50%, ${primaryColor}18, transparent 70%)`
                  : 'none',
            }}
            className="absolute inset-0 rounded-2xl pointer-events-none transition-all duration-300"
          />

          <div className="relative bg-card/90 backdrop-blur-sm border border-color/60 rounded-2xl shadow-xl shadow-slate-200/20 overflow-hidden">
            {/* Barra de gradiente animada */}
            <motion.div
              animate={{
                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
              }}
              transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
              className="h-1.5 bg-gradient-to-r from-emerald-400 via-green-500 to-teal-400 bg-[length:200%_200%]"
            />

            <div className={isMobile ? 'p-4' : 'p-6'}>
              {/* Status + Badge */}
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <motion.div
                    animate={{
                      rotate: hoveredSection === 'status' ? 360 : 0,
                    }}
                    transition={{ duration: 0.6, ease: 'easeInOut' }}
                    className="p-2.5 bg-emerald-50 border border-emerald-200 rounded-xl"
                  >
                    <CheckCircle size={20} className="text-emerald-600" />
                  </motion.div>
                  <div>
                    <h2 className="font-semibold text-primary text-sm sm:text-base">
                      Status da Mensagem
                    </h2>
                    <p className="text-xs text-muted">
                      {message.status === 'read'
                        ? 'Mensagem visualizada pelo cliente'
                        : message.status === 'delivered'
                          ? 'Mensagem entregue no dispositivo'
                          : message.status === 'sent'
                            ? 'Mensagem enviada com sucesso'
                            : message.status === 'failed'
                              ? 'Falha no envio da mensagem'
                              : 'Mensagem em processamento'}
                    </p>
                  </div>
                </div>
                <StatusBadge status={message.status} />
              </div>

              {/* Grid de metadados */}
              <div
                className={`grid ${isMobile ? 'grid-cols-1 gap-2' : 'grid-cols-2 gap-3'}`}
              >
                <MetaItem
                  icon={Calendar}
                  label="Enviada em"
                  value={formatFullDateTime(message.createdAt)}
                />
                {message.template && (
                  <MetaItem icon={Tag} label="Template">
                    <span className="px-2.5 py-0.5 bg-purple-100 text-purple-700 rounded-full text-[10px] font-semibold uppercase">
                      {message.template}
                    </span>
                  </MetaItem>
                )}
                <MetaItem
                  icon={Send}
                  label="Direção"
                  value={
                    message.direction === 'outgoing'
                      ? '📤 Enviada por você'
                      : '📥 Recebida do cliente'
                  }
                />
                {message.phone && (
                  <MetaItem
                    icon={Phone}
                    label="Número de destino"
                    value={message.phone}
                  />
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* ===== CARD DO CLIENTE ===== */}
        {client && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.16 }}
            className="relative mb-4"
          >
            <div className="relative bg-card/90 backdrop-blur-sm border border-color/60 rounded-2xl shadow-xl shadow-slate-200/20 overflow-hidden">
              <div className="h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />
              <div className={isMobile ? 'p-4' : 'p-6'}>
                {/* Header cliente */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-500 flex items-center justify-center text-white text-xl font-bold shadow-lg flex-shrink-0">
                    {client.name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-lg font-bold text-primary truncate">
                      {client.name}
                    </h2>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {client.status && (
                        <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-[10px] font-semibold">
                          {client.status}
                        </span>
                      )}
                      {client.temperature && (
                        <span className="px-2.5 py-0.5 bg-blue-100 text-blue-700 rounded-full text-[10px] font-semibold">
                          {client.temperature}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Botão ver conversa completa */}
                  <button
                    onClick={handleViewFullConversation}
                    className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-xl text-xs font-semibold transition-all active:scale-95"
                  >
                    <History size={14} />
                    <span className="hidden sm:inline">Ver conversa</span>
                  </button>
                </div>

                {/* Grid info cliente */}
                <div
                  className={`grid ${isMobile ? 'grid-cols-1 gap-2' : 'grid-cols-2 gap-3'}`}
                >
                  {client.phone && (
                    <InfoPill icon={Phone} text={client.phone} />
                  )}
                  {client.email && (
                    <InfoPill icon={Mail} text={client.email} />
                  )}
                  {client.city && (
                    <InfoPill icon={MapPin} text={client.city} />
                  )}
                  {client.type && <InfoPill icon={User} text={client.type} />}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* ===== CARD DO CONTEÚDO ===== */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="relative mb-6"
        >
          <div className="relative bg-card/90 backdrop-blur-sm border border-color/60 rounded-2xl shadow-xl shadow-slate-200/20 overflow-hidden">
            {/* Header do card */}
            <div className="relative overflow-hidden">
              <motion.div
                animate={{
                  background: [
                    `linear-gradient(90deg, ${primaryColor}15, ${primaryColor}30, ${primaryColor}15)`,
                    `linear-gradient(90deg, ${primaryColor}30, ${primaryColor}15, ${primaryColor}30)`,
                    `linear-gradient(90deg, ${primaryColor}15, ${primaryColor}30, ${primaryColor}15)`,
                  ],
                }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute inset-0"
              />
              <div className="relative flex justify-between items-center px-5 py-3.5">
                <div className="flex items-center gap-2">
                  <Sparkles size={16} className="text-amber-500" />
                  <h2 className="font-semibold text-primary text-sm">
                    Conteúdo da Mensagem
                  </h2>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      message?.message && copyToClipboard(message.message)
                    }
                    className="p-1.5 hover:bg-card/50 rounded-lg transition-colors relative"
                    aria-label="Copiar conteúdo"
                  >
                    {copied ? (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                      >
                        <Check size={16} className="text-green-600" />
                      </motion.div>
                    ) : (
                      <Copy size={16} className="text-muted" />
                    )}
                  </button>
                  <span className="text-[10px] text-muted font-mono">
                    {message.message?.length || 0} caracteres
                  </span>
                </div>
              </div>
            </div>

            {/* Corpo */}
            <div className={isMobile ? 'p-4' : 'p-6'}>
              <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl border border-color p-5">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    p: ({ node, ...props }) => (
                      <p
                        className="text-secondary leading-relaxed mb-3 last:mb-0 text-sm"
                        {...props}
                      />
                    ),
                    strong: ({ node, ...props }) => (
                      <strong className="font-bold text-primary" {...props} />
                    ),
                    em: ({ node, ...props }) => (
                      <em className="italic text-secondary" {...props} />
                    ),
                    ul: ({ node, ...props }) => (
                      <ul
                        className="list-disc list-inside space-y-1 mb-3"
                        {...props}
                      />
                    ),
                    ol: ({ node, ...props }) => (
                      <ol
                        className="list-decimal list-inside space-y-1 mb-3"
                        {...props}
                      />
                    ),
                    li: ({ node, ...props }) => (
                      <li className="text-secondary text-sm" {...props} />
                    ),
                    a: ({ node, ...props }) => (
                      <a
                        className="text-emerald-600 hover:underline font-medium"
                        target="_blank"
                        rel="noopener noreferrer"
                        {...props}
                      />
                    ),
                    blockquote: ({ node, ...props }) => (
                      <blockquote
                        className="border-l-4 border-emerald-400 pl-4 italic text-secondary my-3 py-1 bg-emerald-50/50 rounded-r-lg"
                        {...props}
                      />
                    ),
                    code: ({ node, inline, ...props }) =>
                      inline ? (
                        <code
                          className="bg-muted text-rose-600 px-1.5 py-0.5 rounded text-xs font-mono"
                          {...props}
                        />
                      ) : (
                        <code
                          className="block bg-slate-800 text-emerald-300 p-4 rounded-xl text-xs overflow-x-auto my-3 font-mono leading-relaxed"
                          {...props}
                        />
                      ),
                  }}
                >
                  {message.message || '*Nenhum conteúdo*'}
                </ReactMarkdown>
              </div>
            </div>

            {/* Footer do card */}
            <div className="px-5 py-3 bg-muted/80 border-t border-color flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 text-[10px] text-muted">
                <Shield size={11} />
                <span>Mensagem registrada</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1 text-[10px] text-muted">
                  <Zap size={11} className="text-amber-500" />
                  WhatsApp
                </span>
                <span className="flex items-center gap-1 text-[10px] text-muted">
                  <Globe size={11} className="text-blue-500" />
                  Via Web
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ===== BOTÕES DE AÇÃO ===== */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.24 }}
          className={`flex gap-3 ${isMobile ? 'flex-col' : ''}`}
        >
          <button
            onClick={() => navigate('/mensagens')}
            className={`
              flex items-center justify-center gap-2
              px-4 py-3 surface-card rounded-2xl
              text-secondary font-semibold text-sm
              hover:bg-muted active:scale-[0.98]
              shadow-md hover:shadow-lg transition-all
              ${isMobile ? 'w-full' : 'flex-1'}
            `}
          >
            <ArrowLeft size={16} />
            Voltar para lista
          </button>

          <button
            onClick={handleViewFullConversation}
            disabled={!client || conversationMessages.length === 0}
            className={`
              flex items-center justify-center gap-2
              px-4 py-3 bg-indigo-50 border border-indigo-200 rounded-2xl
              text-indigo-700 font-semibold text-sm
              hover:bg-indigo-100 active:scale-[0.98]
              shadow-md hover:shadow-lg transition-all
              disabled:opacity-40 disabled:cursor-not-allowed
              ${isMobile ? 'w-full' : 'flex-1'}
            `}
          >
            <History size={16} />
            Ver conversa completa
          </button>

          <button
            onClick={handleDeleteClick}
            disabled={deleting}
            className={`
              flex items-center justify-center gap-2
              px-4 py-3 bg-red-50 border border-red-200 rounded-2xl
              text-red-600 font-semibold text-sm
              hover:bg-red-100 active:scale-[0.98]
              shadow-md hover:shadow-lg transition-all
              disabled:opacity-50 disabled:cursor-not-allowed
              ${isMobile ? 'w-full' : ''}
            `}
          >
            {deleting ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Trash2 size={16} />
            )}
            {deleting ? 'Excluindo...' : 'Excluir'}
          </button>

          {client?.phone && (
            <a
              href={`https://wa.me/${(message.phone || client.phone).replace(/\D/g, '')}?text=${encodeURIComponent(message.message || '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className={`
                flex items-center justify-center gap-2
                px-4 py-3 bg-gradient-to-r from-emerald-500 to-green-600
                text-white font-semibold text-sm rounded-2xl
                hover:brightness-110 active:scale-[0.98]
                shadow-lg shadow-emerald-500/20 hover:shadow-xl
                transition-all
                ${isMobile ? 'w-full' : 'flex-1'}
              `}
            >
              <MessageSquare size={16} />
              Responder no WhatsApp
              <ExternalLink size={12} className="opacity-70" />
            </a>
          )}
        </motion.div>
      </div>

      {/* ================================================================ */}
      {/* MENU CONTEXTUAL (desktop) */}
      {/* ================================================================ */}
      <MessageActionMenu
        isOpen={contextMenu.isOpen}
        onClose={handleCloseContextMenu}
        message={contextMenu.message}
        permissions={permissions}
        onView={() => {}}
        onEdit={handleContextEdit}
        onResend={handleContextResend}
        onCopy={() => message?.message && copyToClipboard(message.message)}
        onDelete={handleDeleteClick}
        triggerRef={contextMenuTriggerRef}
      />

      {/* ================================================================ */}
      {/* MODAL DE EXCLUSÃO UNIFICADO */}
      {/* ================================================================ */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        type="message"
        data={{
          message,
          clientName: client?.name,
          clientPhone: client?.phone,
        }}
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteModal(false)}
        loading={deleting}
        primaryColor={primaryColor}
      />

      {/* ================================================================ */}
      {/* MODAL DE HISTÓRICO DA CONVERSA */}
      {/* ================================================================ */}
      <MessageHistoryModal
        isOpen={showHistoryModal}
        client={client}
        messages={conversationMessages}
        onClose={() => setShowHistoryModal(false)}
        primaryColor={primaryColor}
        onDeleteMessage={async (msgId) => {
          const ctx = getContext();
          if (ctx?.collections?.messages?.delete) {
            await ctx.collections.messages.delete(msgId);
          } else if (database?.deleteMessage) {
            await database.deleteMessage(msgId);
          }
          setConversationMessages((prev) =>
            prev.filter((m) => m.id !== msgId)
          );
          showToast('Mensagem excluída', 'success');
        }}
        onDeleteConversation={async (clientId) => {
          const ctx = getContext();
          const msgsToDelete = conversationMessages.filter(
            (m) => m.clientId === clientId
          );
          for (const msg of msgsToDelete) {
            try {
              if (ctx?.collections?.messages?.delete) {
                await ctx.collections.messages.delete(msg.id);
              } else if (database?.deleteMessage) {
                await database.deleteMessage(msg.id);
              }
            } catch {}
          }
          setConversationMessages([]);
          setShowHistoryModal(false);
          navigate('/mensagens', { replace: true });
          showToast('Conversa excluída', 'success');
        }}
      />
    </div>
  );
}

// ============================================================================
// 🎯 SUBCOMPONENTES AUXILIARES (memoizados)
// ============================================================================
const MetaItem = memo(({ icon: Icon, label, value, children }) => (
  <div className="flex items-center gap-3 p-3 bg-muted/80 rounded-xl border border-color">
    <div className="p-1.5 bg-card rounded-lg shadow-sm">
      <Icon size={16} className="text-muted" />
    </div>
    <div className="min-w-0">
      <p className="text-[10px] text-muted uppercase tracking-wider font-medium">
        {label}
      </p>
      {value ? (
        <p className="font-medium text-primary text-sm truncate">{value}</p>
      ) : (
        children
      )}
    </div>
  </div>
));
MetaItem.displayName = 'MetaItem';

const InfoPill = memo(({ icon: Icon, text }) => (
  <div className="flex items-center gap-2 p-2.5 bg-muted/80 rounded-xl border border-color">
    <Icon size={14} className="text-muted flex-shrink-0" />
    <span className="text-xs text-secondary truncate">{text}</span>
  </div>
));
InfoPill.displayName = 'InfoPill';